/**
 * EntityTypePickerPopover
 *
 * A small, positioned popover (NOT a modal) shown near the user's selection when
 * creating a manual redaction. Lets the user:
 *   1. Edit / type the entity text (pre-filled for text selections, empty for box draws),
 *   2. Pick an entity type from a grid of color-coded pills, and
 *   3. Optionally attach a redaction reason.
 *
 * Styled to match this app's house light theme (white surface, brand purple
 * `#7035C4`) — see the suggestion hover tooltip in PDFViewer.tsx.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus } from 'lucide-react';
import { SearchableReasonSelect } from './SearchableReasonSelect';
import type { EntityType, RedactionReason } from '../../types';
import { getEntityTypeConfig, pickerEntityTypes, useDetectionCategories } from '../../lib/categories';

const LAUNCHPAD_PRIMARY = '#7035C4';
const POPOVER_WIDTH = 340;
/** Initial height guess used before the popover has been measured on first paint. */
const POPOVER_MAX_HEIGHT = 420;
/** Minimum gap kept between the popover and the viewport edges. */
const VIEWPORT_MARGIN = 8;

export interface EntityTypePickerConfirm {
  text: string;
  entityType: EntityType;
  reason?: string;
}

interface EntityTypePickerPopoverProps {
  /** Whether the popover is visible. */
  isOpen: boolean;
  /** Screen position (clientX/clientY) to anchor the popover near. */
  position: { x: number; y: number };
  /** Pre-filled entity text (empty for PDF box draws — user can type it). */
  extractedText?: string;
  /** Page number (0-indexed) for the header label. */
  page?: number;
  /** Reasons feeding the optional reason selector. */
  reasons: RedactionReason[];
  /** Called with the chosen text + type + reason when the user confirms. */
  onConfirm: (data: EntityTypePickerConfirm) => void;
  /** Called when the user cancels (Escape, click-outside, Cancel button). */
  onCancel: () => void;
}

export function EntityTypePickerPopover({
  isOpen,
  position,
  extractedText,
  page,
  reasons,
  onConfirm,
  onCancel,
}: EntityTypePickerPopoverProps) {
  const [selectedType, setSelectedType] = useState<EntityType>('CUSTOM');
  useDetectionCategories(); // the pills list the tenant's enabled categories
  const commonTypes = pickerEntityTypes();
  const [editedText, setEditedText] = useState(extractedText ?? '');
  const [reason, setReason] = useState('');
  const pickerRef = useRef<HTMLDivElement>(null);
  // Actual rendered popover height, measured after paint, so the bottom clamp
  // reflects the real size (which varies with the reason selector) instead of a
  // fixed guess that lets a tall popover run off the bottom of the page.
  const [measuredHeight, setMeasuredHeight] = useState(POPOVER_MAX_HEIGHT);

  // Reset local state whenever the popover (re)opens or its source text changes.
  // The entity text is captured from the selection (extractedText) and carried
  // through to onConfirm, but is no longer user-editable in this dialog.
  useEffect(() => {
    if (isOpen) {
      setSelectedType('CUSTOM');
      setEditedText(extractedText ?? '');
      setReason('');
    }
  }, [isOpen, extractedText]);

  // Escape cancels; Enter confirms (when text is non-empty).
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onConfirm({
          text: editedText.trim(),
          entityType: selectedType,
          reason: reason || undefined,
        });
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, editedText, selectedType, reason, onConfirm, onCancel]);

  // Click-outside cancels. Delayed registration avoids the opening mouseup
  // immediately closing the popover.
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        onCancel();
      }
    };
    const timer = window.setTimeout(
      () => document.addEventListener('mousedown', handleClickOutside),
      100,
    );
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onCancel]);

  // Measure the popover once it's painted (and whenever its content changes the
  // height) so the clamp below can keep it fully on-screen.
  useLayoutEffect(() => {
    if (isOpen && pickerRef.current) {
      setMeasuredHeight(pickerRef.current.offsetHeight);
    }
  }, [isOpen, editedText, selectedType, reason, extractedText]);

  if (!isOpen) return null;

  // Text is optional: a blackout region drawn with the box tool is a valid
  // redaction even with no text (the backend labels it "[Manual Redaction]").
  // So confirming is always allowed.
  const canConfirm = true;

  // Position near the selection, clamped to stay fully on-screen. The bottom
  // clamp uses the measured height so a tall popover never runs off the page;
  // Math.max(VIEWPORT_MARGIN, …) keeps the top from being pushed above the
  // viewport on short windows.
  const maxTop = window.innerHeight - measuredHeight - VIEWPORT_MARGIN;
  const pickerStyle: React.CSSProperties = {
    position: 'fixed',
    left: Math.max(
      VIEWPORT_MARGIN,
      Math.min(position.x, window.innerWidth - POPOVER_WIDTH - VIEWPORT_MARGIN),
    ),
    top: Math.max(VIEWPORT_MARGIN, Math.min(position.y + 10, maxTop)),
    zIndex: 9999,
  };

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm({
      text: editedText.trim(),
      entityType: selectedType,
      reason: reason || undefined,
    });
  };

  return createPortal(
    <div
      ref={pickerRef}
      style={pickerStyle}
      className="bg-white border border-gray-200 rounded-lg shadow-lg text-gray-900 w-[340px] overflow-hidden"
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <Plus className="w-4 h-4" style={{ color: LAUNCHPAD_PRIMARY }} />
        <span className="text-sm font-semibold text-gray-900">Add Entity</span>
        {typeof page === 'number' && (
          <span className="ml-auto text-xs text-gray-400">Page {page + 1}</span>
        )}
      </div>

      {/* Entity type selector */}
      <div className="px-4 py-3 border-b border-gray-100">
        <label className="text-xs font-medium text-gray-500 mb-2 block">
          Entity Type
        </label>
        <div className="grid grid-cols-3 gap-1.5">
          {commonTypes.map((type) => {
            const config = getEntityTypeConfig(type);
            const isSelected = selectedType === type;
            return (
              <button
                key={type}
                type="button"
                onClick={() => setSelectedType(type)}
                title={config.label}
                className="inline-flex items-center justify-center gap-1 px-1.5 py-1 rounded text-[11px] font-medium transition-all"
                style={{
                  backgroundColor: isSelected ? config.color + '20' : '#F9FAFB',
                  color: isSelected ? config.color : '#4B5563',
                  border: `1px solid ${isSelected ? config.color : '#E5E7EB'}`,
                }}
              >
                <span
                  className="inline-block h-1.5 w-1.5 rounded-full shrink-0"
                  style={{ backgroundColor: config.color }}
                  aria-hidden
                />
                <span className="truncate">{config.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Reason (optional) */}
      <div className="px-4 py-3 border-b border-gray-100">
        <label className="text-xs font-medium text-gray-500 mb-1 block">
          Reason <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <SearchableReasonSelect
          reasons={reasons}
          value={reason}
          onChange={setReason}
          placeholder="Select reason..."
        />
      </div>

      {/* Actions */}
      <div className="px-4 py-3 flex items-center justify-between">
        <span className="text-xs text-gray-400">⏎ Enter to confirm</span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="px-4 py-1.5 text-xs font-medium text-white rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: LAUNCHPAD_PRIMARY }}
            onMouseEnter={(e) => {
              if (canConfirm) e.currentTarget.style.backgroundColor = '#5c2a9e';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = LAUNCHPAD_PRIMARY;
            }}
          >
            Add Entity
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
